import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Scroll-reveal hook. Observes `.reveal` elements and adds `.is-visible` when
 * they enter the viewport. Re-runs on route change AND watches the DOM for
 * nodes added later (e.g. async Firestore data) so dynamically-rendered cards
 * still animate in instead of staying hidden.
 */
export default function useScrollReveal() {
  const { pathname } = useLocation();
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );

    const observeAll = () => {
      document
        .querySelectorAll(".reveal:not(.is-visible)")
        .forEach((el) => io.observe(el));
    };

    // Initial pass after the new page mounts.
    const rafId = requestAnimationFrame(observeAll);

    // Catch elements inserted after the initial pass (async data loads).
    const mo = new MutationObserver(() => observeAll());
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(rafId);
      mo.disconnect();
      io.disconnect();
    };
  }, [pathname]);
}
